// ══════════════════════════════════════════════════════════════════
//  부품 상점 — 물맷돌 전용 (`아흔아홉_물맷돌강화트리_설계안.md` 6장)
//
//  ■ **`shop.js`(카드 상점)를 고치지 않고 따로 세운다.** 카드 쪽 로직(중첩·만렙 제외·
//    가중치 15.5·결 3택·진화·활성 풀 로테이션)이 부품과 **거의 안 겹치고**,
//    지팡이가 아직 카드를 쓰므로(설계안 11장 보류) 카드 쪽을 지울 수도 없다.
//    한 클래스에 섞으면 둘 다 지저분해진다.
//
//  ■ **인터페이스는 `Shop` 과 같게 맞춘다** — `request/pump/offers/buy/reroll/close/
//    price/wallet/report`. 그래야 `scene.js` 와 `shopui.js` 가 거의 안 바뀐다.
//    `offers[i].card` 모양까지 맞춘다(표현층이 그것을 읽는다).
//    ⚠ 【2026-08-01 감사】 위 두 줄은 **그때의 사정**이다. `Shop`(카드 상점)도
//      `shopui.js` 도 2026-07-31 지팡이·카드 삭제와 함께 없어졌다. 지금 이 파일의
//      표현층은 `gearui.js` 하나다. 인터페이스 모양은 그대로 두지만, 「같게 맞출
//      상대」는 이제 없다는 것을 알고 읽을 것.
//
//  ■ **카드 상점과 다른 것 넷**
//      ① 제시는 **네 부위 전부, 자리 고정**이다. 구매 상한 3회라 하나는 버린다
//      ② 사면 **그 칸의 이전 부품이 빠진다** — 중첩이 아니라 교체다
//      ③ **잠금 1개** — 「따로 챙겨 둔다」. 리롤에도, 다음 상점에도 남는다
//      ④ 등급은 **밤 시계**가 정한다. 레벨업 횟수(=경제)에 안 걸린다
//
//  ■ 가격은 **장착 결과 등급**을 쓴다. 같은 5성 부품을 다시 사서 ◆가 되면
//    놓인 카드가 5성이어도 ◆ 가격이다. 장·구매 순서·UI가 별도 가격을 만들지 않는다.
// ══════════════════════════════════════════════════════════════════
import { SHOP, gearPriceAt, NIGHT_NO, NIGHT_SEC, rerollPriceAt } from './config.js';

// ── 리롤 가격 ─────────────────────────────────────────────────────
// 【2026-08-14 단일화】 UI와 자동 구매의 반올림 시점이 갈리지 않도록 config의 한 함수를 쓴다.
export { rerollPriceAt };
import { SLOTS, SLOT_KO, PARTS, TIER_DIAMOND, rollOffers, rollPart, startGear, resolveGear, looseSlots, dots, CLS_KO, TRAIT_BY_ID, stoneAxis,
         isUpgrade, upgraded, setOf, stonePierceValue } from './gear.js';   // 【08-01】 같은 부품 재구매 = 등급 상승
import { compareLoadouts } from './gearstats.js';

// `shop.js` 의 것과 같은 mulberry32. **일부러 복제한다** — 부품 추첨이 스폰 난수열을
// 건드리면 같은 시드에서 밤이 달라진다.
function mulberry(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// 카드 생성·미리보기·구매 검사가 같은 장착 후 모양을 쓴다. 클래스 메서드로만 두면
// 비교 전수검사가 `preview`를 최소 객체에 빌려 쓸 때 배선 하나가 빠질 수 있다.
function gearAfterPart(gear, o) {
  if (!o) return gear;
  const worn = gear[o.slot];
  // 미리보기에는 새 특성의 무작위 결과를 지어내지 않는다. 등급과 기존 특성만 올려
  // 5성 → ◆의 순수한 변화와 큰 세트 유지(5·5·◆·5)를 정확히 보여 준다.
  const next = isUpgrade(worn, o)
    ? { p: worn.p, t: worn.t + 1, traits: (worn.traits ?? []).slice() }
    : { p: o.p, t: o.t, traits: o.traits };
  return { ...gear, [o.slot]: next };
}

// 가격·미리보기·실제 구매가 모두 이 결과 등급을 읽는다. 놓인 별만 보면 같은 부품
// 재구매의 5→◆를 5성 가격으로 잘못 받게 된다.
export function resultTierForPart(gear, o) {
  if (!o?.slot) return 1;
  const worn = gear?.[o.slot];
  return isUpgrade(worn, o)
    ? Math.min(TIER_DIAMOND, (worn.t | 0) + 1)
    : Math.max(1, Math.min(TIER_DIAMOND, o.t | 0));
}

export class GearShop {
  constructor(world, opts = {}) {
    this.w = world;
    this.weapon = opts.weapon ?? world.weapon;
    this.nightKey = opts.nightKey ?? world.nightKey;
    this.nightNo = NIGHT_NO[this.nightKey] ?? (parseInt(String(this.nightKey).slice(1), 10) || 1);
    this.seed = opts.seed ?? 20260728;
    this.rnd = mulberry(this.seed);

    // 장비 4칸. world 가 이미 만들어 뒀으면 그것을 쓴다(같은 물건을 둘이 갖지 않는다).
    this.gear = world.gear ?? startGear();

    this.queue = 0;
    this.open = false;
    this.offers = [];
    this.buysLeft = 0;
    this.sessionBought = 0;
    // 입장 지갑이 0이던 밤의 첫 구매 세션 한 번만 검사한다. 첫 상점이 열릴 때는
    // 이미 조각이 1~2치즈가 되었어도 이 시작 조건을 잃으면 안 된다.
    this._coldStartInsurancePending = Math.max(0, Number(world.walletCarry) || 0) === 0;

    // ── 【2026-08-06】 귀한 돌 — *"내일 밤, 첫 진열에 놓여 있다"* ──
    //   낮이 opts.jackpot(=world.jackpot) 을 보내면 그 밤 **첫 제시**의 한 칸이
    //   ●●●●● 부품으로 고정된다. 첫 부품이 빌드의 씨앗이므로, 같은 수식어의 짝을
    //   그 뒤에 억제하지 않는다. 일반 2/4·3/4 부족 슬롯 보정은 gear.rollPart가 맡는다.
    this._jackpot = (opts.jackpot ?? world.jackpot) ? true : false;

    // ── 잠금 「따로 챙겨 둔다」 ──────────────────────────────────
    // **1개.** 둘 이상이면 상점을 통째로 붙들게 된다. 공짜다 — 1개 제한이 이미 제약.
    // 리롤에도 다음 세션에도 남고, **밤은 안 넘는다**(리셋 불가침).
    this.locked = null;          // 잠긴 제시 객체(슬롯 통째)
    // 부품에는 **결 3택이 없다.** gearui 가 이 값으로 화면 문구를 가르므로 명시한다.
    //   (구 주석은 `shopui` — 7-31 에 삭제된 파일이다. 08-01 감사에서 고쳤다.)
    this.kyeolSession = false;

    this.m = {
      sessions: 0, buys: 0, rerolls: 0, rerollSpent: 0,
      locks: 0, swaps: {}, tierSum: 0, tierN: 0, skippedSessions: 0, brokeSessions: 0,
      // 같은 부품을 또 사서 등급이 오른 횟수 — **얼마나 드문 잭팟인지 재는 자리**다.
      //   경제를 마지막에 한 번에 잡을 때 이 수가 근거가 된다(값은 지금 안 건드린다).
      upgrades: 0, coldStartInsured: 0,
    };
    for (const s of SLOTS) this.m.swaps[s] = 0;
    this.syncWorld();
  }

  // ── 장부 ────────────────────────────────────────────────────────
  get wallet() { return this.w.wallet; }
  resultTier(part) { return resultTierForPart(this.gear, part); }
  priceForPart(part) { return gearPriceAt(this.resultTier(part)); }
  priceFor(i) {
    const part = this.offers[i]?.part;
    return part ? this.priceForPart(part) : Infinity;
  }
  canAffordOffer(i) {
    const offer = this.offers[i];
    return !!offer && !offer.bought && this.buysLeft > 0 && this.wallet >= this.priceFor(i);
  }
  canAffordAny() {
    return this.offers.some((_, i) => this.canAffordOffer(i));
  }
  // 구 단일가 API를 읽는 진단 도구용 호환값. 실제 구매는 반드시 `priceFor(i)`를 쓴다.
  get price() {
    const prices = this.offers
      .map((offer, i) => offer.bought ? Infinity : this.priceFor(i))
      .filter(Number.isFinite);
    return prices.length ? Math.min(...prices) : gearPriceAt(1);
  }
  get nextRerollPrice() { return rerollPriceAt(this.w.rerollsThisNight + 1); }
  get canAfford() { return this.canAffordAny(); }
  // **실제로 낀 것이 정본이다** — 모델(rampGear)은 사람이 고르는 경로에서 안 쓴다.
  syncWorld() {
    this.w.gear = this.gear;
    this.w.gearKinds = null;         // 모델 갱신을 끈다. 이제 사람이 정한다
    this.w.refreshWeapon?.();
  }

  // 밤 진행률 — **등급 게이팅의 유일한 입력.** 경제가 어디로 착지하든 안 움직인다.
  get u() { return Math.max(0, Math.min(1, (this.w.t ?? 0) / NIGHT_SEC)); }

  request() { this.queue++; }

  pump() {
    if (this.open || this.queue <= 0) return this.open;
    // ⚠ **「그러모으기」가 빨아들이는 동안은 안 연다.** 1.6초에 걸쳐 조각이 들어오므로
    //   그때마다 열면 레벨업이 **시간에 퍼져** 연속으로 안 읽힌다(실측: 최대 1단계).
    //   다 빨아들인 뒤 한꺼번에 열어야 *"한꺼번에 N단계 올랐다"* 가 사건이 된다 —
    //   VS 가 자석 뒤에 rapid-fire level-up 을 몰아 보여 주는 이유가 그것이다.
    if (this.w.t < (this.w._gatherUntil ?? -9)) return false;
    // ── **연속 레벨업** — 쌓인 것을 한 번에 연다 ────────────────
    // 「그러모으기」로 조각이 한꺼번에 들어오면 레벨업이 연달아 터진다. 그때
    // 상점을 세 번 여닫으면 **카타르시스가 끊긴다** — VS 가 rapid-fire level-up 을
    // 하나의 사건으로 보여 주는 이유가 그것이다.
    // → 큐를 통째로 소진하고 **구매 횟수를 합산**한다. 화면은 그만큼 크게 번쩍인다.
    const burst = this.queue;
    this.queue = 0;
    this.open = true;
    this.buysLeft = SHOP.buysPerLevel * burst;   // 4-3 — 레벨업당 3회. 상한이지 보장이 아니다
    this.burst = burst;
    this.sessionBought = 0;
    this.draw();
    if (this.offers.length === 0) { this.close(); return false; }
    this.m.sessions++;
    this.m.bursts = Math.max(this.m.bursts ?? 0, burst);
    this.w.shopOpens += burst;
    this.w.buysWanted += SHOP.buysPerLevel * burst;
    return true;
  }

  // ── 제시 — **네 부위 전부, 자리 고정** ──────────────────────────
  // 매 제시에 돌·받침·끈·고리가 하나씩 뜬다. 구매 상한은 3회라 하나는 반드시 버린다.
  draw(isReroll = false) {
    const keep = this.locked ?? null;
    // **지금 낀 것을 넘긴다** — 상점이 맞는 짝을 더 자주 낸다(gear.rollPart 주석)
    const fresh = rollOffers(this.rnd, this.u, keep, this.gear);
    // 귀한 돌 — 밤의 첫 제시에서 딱 한 번, 한 칸을 ●5 로 갈아 끼운다.
    if (this._jackpot) {
      this._jackpot = false;
      const i = Math.floor(this.rnd() * fresh.length);
      const five = rollPart(this.rnd, fresh[i].slot, this.u, this.gear);
      five.t = 5;
      while (five.traits.length < 2) {           // ●5 는 특성 2칸 — 규격을 지킨다
        const pool = TRAIT_BY_ID ? Object.keys(TRAIT_BY_ID) : [];
        const cand = pool.filter((id) => !five.traits.includes(id));
        if (!cand.length) break;
        five.traits.push(cand[Math.floor(this.rnd() * cand.length)]);
      }
      five.jackpot = true;                       // cold-start 보험이 이 칸을 덮지 않는다
      fresh[i] = five;
    }
    this.offers = fresh.map(o => ({ card: this.face(o), part: o, bought: false }));
    if (this._coldStartInsurancePending && !isReroll) {
      this._coldStartInsurancePending = false;
      this.ensureColdStartOffer();
    }
    for (const { part } of this.offers) { this.m.tierSum += part.t; this.m.tierN++; }
  }

  // 0치즈로 입장한 밤의 첫 구매 세션이 전부 비싼 카드면, 정상 추첨을 다시 하지 않고
  // 가장 낮은 비보호 칸 하나만 1치즈짜리 **비강화 1성 결과**로 만든다. 이미 살 수 있는
  // 카드가 하나라도 있으면 아무 일도 하지 않는다. 귀한 돌과 챙겨 둔 카드는 후보에서
  // 제외하므로 보험이 행운이나 플레이어 결정을 덮지 않는다.
  ensureColdStartOffer() {
    if (this.canAffordAny()) return false;
    const candidates = this.offers
      .map((offer, i) => ({ offer, i, tier: this.resultTier(offer.part) }))
      .filter(({ offer }) => offer.part !== this.locked && !offer.part.jackpot)
      .sort((a, b) => a.tier - b.tier || a.i - b.i);
    const target = candidates[0]?.offer;
    if (!target) return false;
    const slot = target.part.slot, worn = this.gear[slot];
    // 같은 1성 부품이면 구매 결과가 2성이 되어 2치즈다. 그 드문 경우에만 같은 슬롯의
    // 다른 종류를 골라 결과 자체가 1성이 되게 한다.
    const partDef = target.part.p === worn?.p
      ? (PARTS[slot].find((p) => p !== worn.p) ?? target.part.p)
      : target.part.p;
    const insured = { slot, p: partDef, t: 1, traits: [], coldStart: true };
    target.part = insured;
    target.card = this.face(insured);
    this.m.coldStartInsured++;
    return true;
  }

  // ── 표현층이 읽는 모양 ──────────────────────────────────────────
  // 표현층은 `offers[i].card` 의 `ko/per/tail` 을 읽는다. 부품을 그 모양으로
  // 감싸면 **UI를 한 줄도 안 고치고** 돌아간다.
  //   ⚠ 【2026-08-01 감사】 여기 `shopui.js` 라고 적혀 있었는데 **그 파일은 지팡이
  //     삭제(7-31) 때 없어졌다.** 지금 읽는 것은 `gearui.js` 다(579행이 `c.per` 을
  //     칸에 앉힌다 — 확인했다). 주석이 없는 파일을 가리키면 다음 사람이 헛짚는다.
  face(o) {
    const p = o.p, cur = this.gear[o.slot];
    const after = gearAfterPart(this.gear, o);
    const afterResolved = resolveGear(after);
    const loose = looseSlots(after);
    const lines = [];
    if (o.slot === 'stone') lines.push(`무게 ${p.w} · 급 ${CLS_KO[p.cls]}`);
    else lines.push(`급 ${CLS_KO[p.cls]}`);
    // ── 【2026-07-30】 **돌의 성질은 이제 「등급이 무엇을 미는가」다** ──────
    //   구 코드는 `p.pierce`·`p.shards`·`p.ramp`·`p.carry` 를 읽었는데 그 넷이
    //   부품 정의에서 **사라졌다**(성질값이 `gear.STONE_AXIS` 한 곳으로 갔다).
    //   그대로 두면 네 줄이 전부 죽은 배선이 되어 **돌 카드가 무게·급만 말한다.**
    //   → 그 돌의 축을 **지금 등급값 그대로** 보여 준다. 카드가 「●●●●● 를 사면
    //     무엇이 얼마나 되는가」를 직접 말하게 된다.
    // ⚠ 세트 표시(같은 수식어가 몇 칸인지·발동했는지)는 **F. 막대 UI 단계 소관**이라
    //   여기서 안 만든다.
    if (o.slot === 'stone') {
      const A = stoneAxis(p.id, o.t);
      if (A.key) lines.push(`${A.ko} ${A.key === 'rate' ? `×${A.v.toFixed(2)}` : Math.round(A.v * 10) / 10}`);
      if (p.pierceInf) lines.push('뚫고 사거리 끝까지 간다');
      else if (stonePierceValue(p) !== null) lines.push('관통 +1');
      if (p.small)     lines.push('여러 개 물린다');
    }
    for (const id of o.traits) lines.push(`· ${TRAIT_BY_ID[id]?.ko ?? id}`);
    // ── 같은 부품이라 **등급이 오르는** 자리인가 【사장님 지시 2026-08-01】 ──
    // ══════════════════════════════════════════════════════════════
    //  ⚠⚠ **카드는 놓인 물건을 있는 그대로 말한다** 【사장님 지시 2026-08-02 — 되돌림】
    //
    //  첫 판은 이 칸을 **오른 뒤 등급으로** 그렸다(이름·점·테두리·이름색 전부).
    //  그건 거짓이다 — 그 칸에 실제로 놓인 물건은 **지금 낀 것과 같은 등급**이고,
    //  그렇게 그리면 **옆 칸의 진짜 상위 등급 카드와 구별이 안 된다.** 잭팟을 눈에
    //  띄게 하려던 것이 오히려 정보를 흐렸다.
    //  사장님 지시: *"동일한 등급의 파츠(예전 버전)으로 하되, 거기 위에 뭔가
    //  강화 가능하다는 표시 정도만. 그리고 사면 지금 장착 파츠에 반영되는 느낌으로."*
    //
    //  → **역할을 가른다.**
    //      카드    = 「무엇이 놓였나」 (사실만. 등급도 색도 원래 것)
    //      장착 칸 = 「사면 무엇이 되나」 (사는 순간 거기서 오른다 — gearui 의 equipFx)
    //    카드가 하는 일은 `upgrade` 를 실어 보내는 것까지다. **꾸미는 것은 UI 소관.**
    // ══════════════════════════════════════════════════════════════
    const up = isUpgrade(cur, o);
    return {
      id: `${o.slot}:${p.id}:${o.t}`,
      ko: `${p.ko} ${dots(o.t)}`,
      per: lines.join(' · '),
      // 사실만 넘긴다 — 배지·테두리는 `gearui` 가 이 값을 보고 그린다.
      upgrade: up ? { from: cur.t, to: cur.t + 1 } : null,
      // **끼면 무엇이 빠지는지 같이 보인다** — 교체이지 누적이 아니라는 것을 화면이 말한다.
      //   ⚠ 강화되는 칸도 **이 줄을 그대로 쓴다.** 그러면 꼬리줄이 카드와 **똑같은 이름·
      //     똑같은 점**을 되뇌게 되고, 그 일치 자체가 「같은 것이다」를 말한다.
      //     따로 문장을 쓰지 않는다(사장님이 표시는 배지·테두리로만 하라고 했다).
      tail: cur ? `${SLOT_KO[o.slot]} — 지금 ${cur.p.ko} ${dots(cur.t)}` : SLOT_KO[o.slot],
      // 모든 급을 장착한다. 낮은 급 후보는 봉인하지 않고, 장착 뒤 실제 정본의 돌 피해
      // 전달과 헐거운 부위를 표현층에 넘겨 손실을 고르기 전에 읽게 한다.
      grip: {
        value: afterResolved.support,
        text: `돌 피해 전달 ${Math.round(afterResolved.support * 100)}%`,
        loose,
      },
      slot: o.slot, blocked: false,
      // ⚠ 【2026-08-01 감사】 **이 플래그를 읽는 데가 없다.** 원 주석은 *"shopui 가
      //   무기 카드로 강조한다"* 였는데 `shopui.js` 는 7-31 에 삭제됐고, 뒤를 이은
      //   `gearui.js` 는 `weapon` 을 한 번도 안 본다(확인했다). 강조가 사라진 것이다.
      //   **안 지운다** — 돌을 무기 자리로 표시하는 것이 옳고, 지우면 다시 강조할 때
      //   근거가 없어진다. 「배선만 있고 주인이 없는 값」으로 여기 적어 둔다.
      weapon: true,
      impl: true,            // 부품은 전부 월드에 먹는다(카드 시절의 `impl` 자리)
    };
  }

  // ── 오버 비교 — **지금 낀 것 대비 뭐가 얼마나 바뀌나** ──────────
  // 【사장님 지시】 *"올라가는건 위 삼각형 초록, 내려가는건 아래 삼각형 빨강."*
  //
  // **색과 삼각형은 같은 것을 가리킨다.** 둘 다 플레이어에게 이득인가 손해인가다.
  //   이득 = 초록 ▲ · 손해 = 빨강 ▼. 숫자가 실제로 오르고 내리는 방향은
  //   `현재값 → 장착 후 값`이 따로 말한다. 그래서 확산이 줄어 좋아지면 `초록 ▲`다.
  //
  // **발 수와 종합 화력을 함께 띄운다.** `주력 위력/초 = 한 발 × 발 수 × 연사`가
  //   전체 결과를 말하고, 발 수는 그 결과가 왜 바뀌었는지를 설명한다.
  //
  // **바뀌지 않은 항목은 안 보여준다** — 카드가 좁고, 안 변한 줄은 소음이다.
  gearAfter(i) {
    const slot = this.offers[i];
    if (!slot?.part || slot.bought) return this.gear;
    return gearAfterPart(this.gear, slot.part);
  }

  preview(i) {
    const o = this.offers[i]?.part;
    if (!o) return null;
    // 화면과 전투가 다른 계산을 갖지 않게 실제 world 식을 번역한 한 모델만 쓴다.
    // 화살표도 여기서 성능 방향으로 확정한다: 이득은 항상 초록 ▲, 손해는 항상 빨강 ▼.
    const axis = 1 + 0.1 * Math.min(1, Math.max(0, this.w?.axis ?? 0));
    return compareLoadouts(this.gear, this.gearAfter(i), {
      slot: o.slot,
      axis,
      dayIn: this.w?.dayIn,
      maxRows: 15,
    }).rows;
  }

  // ── 구매 = **장착.** 그 칸의 이전 부품이 빠진다 ─────────────────
  buy(i) {
    if (!this.open) return false;
    const slot = this.offers[i];
    if (!slot || slot.bought) return false;
    if (this.buysLeft <= 0) return false;
    const price = this.priceFor(i);
    if (this.wallet < price) return false;

    const beforeSet = setOf(this.gear);

    // **장부는 `shop.js` 와 같은 경로로 쓴다** — `wallet` 은 getter 다
    // (= floor(piecesHeld / perCheese) − spent). 직접 못 깎는다.
    this.w.spent += price;
    this.w.buysThisNight++;

    const o = slot.part;
    // ── 같은 부품을 또 샀는가 — **등급이 오른다** 【사장님 지시 2026-08-01】 ──
    //   종류·등급이 둘 다 같을 때만 걸리는 잭팟이다. 규칙은 `gear.js` 한 곳에 있다
    //   (`isUpgrade`/`upgraded` — 등급 상한과 특성 칸 수가 거기 살기 때문이다).
    const worn = this.gear[o.slot];
    const up = isUpgrade(worn, o);
    this.gear[o.slot] = up ? upgraded(worn, this.rnd)
                           : { p: o.p, t: o.t, traits: o.traits };
    const got = this.gear[o.slot];
    this.m.swaps[o.slot]++;
    this.m.buys++;
    if (up) this.m.upgrades++;
    // ── **방금 무엇이 어느 칸에 앉았는가** 【사장님 지시 2026-08-02】 ──────
    //   장착 칸이 이걸 보고 그 자리에서 번쩍이고 점을 채운다(gearui 의 equipFx).
    //   ⚠ **시각(`w.t`)이 아니라 일련번호를 남긴다.** 상점이 열려 있는 동안은
    //     `world.step` 을 아예 안 부르므로 **세계 시간이 멈춰 있다**(scene.js 의
    //     「전투는 멈춘다」 규율). 시각으로 남기면 연출이 한 프레임도 안 움직인다.
    //     UI 는 이 번호가 바뀐 것만 보고 **자기 시계**로 연출을 돌린다.
    const afterSet = setOf(this.gear);
    const setActivated = !!afterSet && (!beforeSet
      || beforeSet.id !== afterSet.id || beforeSet.level !== afterSet.level);
    this.lastEquip = {
      seq: (this.lastEquip?.seq ?? 0) + 1,
      slot: o.slot,
      from: up ? worn.t : (worn?.t ?? null),   // 바뀌기 전 등급(없었으면 null)
      to: got.t,
      upgrade: up,
      // 세트 발동 패널은 실제 판정 정본을 그대로 받는다. UI가 옛 수식어를 따로
      // 외우지 않으므로 이름이 바뀌어도 SET_KO/SET_FX_KO 한 곳만 따라간다.
      setActivated,
      set: afterSet ? { id: afterSet.id, ko: afterSet.ko, fx: afterSet.fx,
                        diamondFx: afterSet.diamondFx,
                        level: afterSet.level } : null,
    };
    // **장착 순간을 화면이 안다** — scene 의 국소 무기 궤도와 강화창 패널이 반응한다.
    // 화면 전체 색 채움은 쓰지 않으며, 실제로 낀 등급과 세트 판정만 사건으로 남긴다.
    this.w._equipAt = this.w.t; this.w._equipTier = got.t; this.w._equipSlot = o.slot;
    slot.bought = true;
    this.buysLeft--;
    this.sessionBought++;
    // **한 장이라도 산 레벨업이 「집은」 레벨업이다**(world.openShop 과 같은 정의).
    if (this.sessionBought === 1) this.w.picksThisNight++;
    if (this.locked === o) this.locked = null;       // 챙겨 둔 것을 샀으면 풀린다
    this.syncWorld();
    // 낀 것이 바뀌었으므로 남은 제시의 「지금 무엇이 빠지나」도 다시 그린다
    for (const s of this.offers) if (!s.bought) s.card = this.face(s.part);
    // 마지막 구매가 세트를 완성했다면 창을 즉시 닫지 않는다. 패널 발동을 사람이
    // 확인한 뒤 Space/B로 닫게 한다. 그 외에는 기존처럼 마지막 구매 즉시 닫힌다.
    if (this.buysLeft <= 0 && !setActivated) this.close();
    return true;
  }

  // ── 잠금 「따로 챙겨 둔다」 ──────────────────────────────────────
  // 표현층은 실제 보관 중인 한 카드에만 작은 자물쇠를 붙인다.
  lock(i) {
    if (!this.open) return false;
    const slot = this.offers[i];
    if (!slot || slot.bought) return false;
    this.locked = (this.locked === slot.part) ? null : slot.part;   // 다시 누르면 풀린다
    if (this.locked) this.m.locks++;
    return true;
  }
  isLocked(i) { return !!this.locked && this.offers[i]?.part === this.locked; }

  // ── 리롤 ────────────────────────────────────────────────────────
  // **잠근 것은 그 자리에 남는다.** 나머지만 다시 뽑는다.
  // `gearui.js` 가 **「왜 못 누르는지」를 갈라 적으려고** 이것을 따로 묻는다 —
  //   (591행 `canRedraw()` → 596행 「리롤 불가 — 바뀔 자리가 없다」. 08-01 확인.
  //    구 주석은 `shopui.js` 라고 적었는데 그 파일은 7-31 에 삭제됐다.)
  // 「바뀔 조합이 없다」와 「돈이 없다」는 다른 사건이다.
  // 부품 풀은 마르지 않는다(만렙 제외가 없고 같은 부위를 계속 갈아끼운다).
  // 단 **셋을 다 잠글 수는 없으므로** 잠기지 않은 자리가 하나라도 있으면 바뀐다.
  canRedraw() { return this.open && this.buysLeft > 0
    && this.offers.some(o => !o.bought && o.part !== this.locked); }
  canReroll() { return this.canRedraw() && this.wallet >= this.nextRerollPrice; }
  reroll() {
    if (!this.canReroll()) return false;
    const p = this.nextRerollPrice;
    this.w.spent += p;
    this.w.rerollsThisNight++;
    this.m.rerolls++; this.m.rerollSpent += p;
    this.draw(true);
    return true;
  }

  close() {
    if (!this.open) return;
    if (this.sessionBought === 0) {
      // 지갑이 비어 한 장도 못 산 세션과 그냥 지나친 세션을 갈라 센다.
      if (!this.canAffordAny()) this.m.brokeSessions++;
      else this.m.skippedSessions++;
    }
    this.open = false;
    this.offers = [];
  }

  // ⚠⚠ **구 카드 상점 API 스텁 셋이 여기 있었다** 【폐기 2026-08-07】.
  //   `axisStack()`·`evolve()`·`stackOf()` — 부품 체계에는 대응물이 없어 늘 0/false 를
  //   돌려주던 자리다. `evolve`·`stackOf` 는 부르는 곳이 **0** 이었고, `axisStack` 은
  //   더 나빴다: 씬이 HUD 와 밤끝 보고에 「sRate 0 · sAlt 0」을 **영구히 찍고 있었다** —
  //   측정이 아니라 **거짓 계측**이다. 씬의 그 두 자리도 같이 걷었다.

  report() {
    const g = this.gear;
    return {
      '장비': SLOTS.map(s => `${g[s].p.ko}${g[s].t}`).join(' · '),
      '평균 등급': (this.m.tierN ? this.m.tierSum / this.m.tierN : 0).toFixed(2),
      '상점 세션': this.m.sessions,
      '구매(교체)': this.m.buys,
      '리롤': `${this.m.rerolls}회 ${this.m.rerollSpent}치즈`,
      '첫 상점 보험': this.m.coldStartInsured,
      '챙겨 둠': this.m.locks,
      '못 산 세션': `${this.m.brokeSessions} (그냥 지나침 ${this.m.skippedSessions})`,
      '돌 피해 전달': (this.w.mods?._gear?.support ?? 1).toFixed(2),
    };
  }
}
